import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const StudentProfile = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const student = location.state?.student;

  const [profile, setProfile] = useState({});
  const [achievements, setAchievements] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    bio: '',
    interests: '',
    emergencyContact: '',
    phoneNumber: ''
  });

  useEffect(() => {
    if (!student) {
      navigate('/student-login');
      return;
    }
    loadProfileData();
  }, [student, navigate]);

  const loadProfileData = async () => {
    try {
      const [profileRes, achievementsRes, enrollmentsRes] = await Promise.all([
        fetch(`${API_ENDPOINTS.BASE_URL}/api/profiles/${student.username}`),
        fetch(`${API_ENDPOINTS.BASE_URL}/api/achievements/${student.username}`),
        fetch(`${API_ENDPOINTS.BASE_URL}/api/enrollments/${student.username}`)
      ]);

      const profileData = await profileRes.json();
      const achievementsData = await achievementsRes.json();
      const enrollmentsData = await enrollmentsRes.json();

      setProfile(profileData);
      setAchievements(achievementsData);
      setEnrollments(enrollmentsData);

      // Initialize edit form
      setEditForm({
        bio: profileData.bio || '',
        interests: profileData.interests || '',
        emergencyContact: profileData.emergencyContact || '',
        phoneNumber: profileData.phoneNumber || student.parentContact || ''
      });
    } catch (error) {
      console.error('Error loading profile data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const updatedProfile = {
        ...profile,
        ...editForm,
        username: student.username,
        firstName: student.firstName,
        lastName: student.lastName,
        className: student.className
      };

      await fetch(`${API_ENDPOINTS.BASE_URL}/api/profiles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedProfile)
      });

      setProfile(updatedProfile);
      setEditing(false);
      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    }
  };

  const generateStudentId = () => {
    return `STU-${student.username.toUpperCase()}-${new Date().getFullYear()}`;
  };

  if (!student) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-blue-50 flex items-center justify-center">
        <div className="text-xl text-blue-600">Loading profile...</div>
      </div>
    );
  }

  const studentId = generateStudentId();

  return (
    <div className="min-h-screen bg-blue-50 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-blue-900">Student Profile</h1>
          <button
            onClick={() => navigate('/student-dashboard', { state: { student } })}
            className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
          >
            Back to Dashboard
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Student ID Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6 text-center">
              <div className="w-32 h-32 bg-blue-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="text-4xl text-blue-600 font-bold">
                  {student.firstName.charAt(0)}{student.lastName.charAt(0)}
                </span>
              </div>
              <h2 className="text-xl font-bold text-gray-800">{student.firstName} {student.lastName}</h2>
              <p className="text-gray-600 mb-2">{student.className}</p>
              <div className="bg-blue-50 p-3 rounded-lg mb-4">
                <p className="text-sm text-gray-600">Student ID</p>
                <p className="text-lg font-mono font-bold text-blue-600">{studentId}</p>
              </div>
              <div className="space-y-2 text-sm text-gray-600">
                <p><strong>Username:</strong> {student.username}</p>
                <p><strong>Age:</strong> {student.age}</p>
                <p><strong>Parent Contact:</strong> {student.parentContact}</p>
              </div>
            </div>
          </div>

          {/* Profile Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-800">Profile Information</h3>
                {!editing && (
                  <button
                    onClick={() => setEditing(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
                  >
                    Edit Profile
                  </button>
                )}
              </div>

              {editing ? (
                <form onSubmit={handleEditSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                    <textarea
                      value={editForm.bio}
                      onChange={(e) => setEditForm({...editForm, bio: e.target.value})}
                      className="w-full border rounded p-2"
                      rows="3"
                      placeholder="Tell us about yourself..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Interests</label>
                    <input
                      type="text"
                      value={editForm.interests}
                      onChange={(e) => setEditForm({...editForm, interests: e.target.value})}
                      className="w-full border rounded p-2"
                      placeholder="e.g., Sports, Music, Science..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <input
                      type="tel"
                      value={editForm.phoneNumber}
                      onChange={(e) => setEditForm({...editForm, phoneNumber: e.target.value})}
                      className="w-full border rounded p-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Emergency Contact</label>
                    <input
                      type="tel"
                      value={editForm.emergencyContact}
                      onChange={(e) => setEditForm({...editForm, emergencyContact: e.target.value})}
                      className="w-full border rounded p-2"
                    />
                  </div>
                  <div className="flex space-x-2">
                    <button
                      type="submit"
                      className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                    >
                      Save Changes
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditing(false)}
                      className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-3">
                  <div>
                    <strong className="text-gray-700">Bio:</strong>
                    <p className="text-gray-600 mt-1">{profile.bio || 'No bio added yet.'}</p>
                  </div>
                  <div>
                    <strong className="text-gray-700">Interests:</strong>
                    <p className="text-gray-600 mt-1">{profile.interests || 'No interests specified.'}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <strong className="text-gray-700">Phone:</strong>
                      <p className="text-gray-600 mt-1">{profile.phoneNumber || 'Not provided'}</p>
                    </div>
                    <div>
                      <strong className="text-gray-700">Emergency Contact:</strong>
                      <p className="text-gray-600 mt-1">{profile.emergencyContact || 'Not provided'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Achievements */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Achievements & Awards</h3>
              {achievements.length === 0 ? (
                <p className="text-gray-600">No achievements yet. Keep up the great work!</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {achievements.map((achievement) => (
                    <div key={achievement.id} className="border rounded-lg p-4 bg-yellow-50">
                      <div className="flex items-center mb-2">
                        <span className="text-2xl mr-3">Award</span>
                        <div>
                          <h4 className="font-bold text-gray-800">{achievement.title}</h4>
                          <p className="text-sm text-gray-600">
                            {new Date(achievement.date).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      {achievement.description && (
                        <p className="text-sm text-gray-700">{achievement.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Enrollments */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Program Enrollments</h3>
              {enrollments.length === 0 ? (
                <p className="text-gray-600">No special program enrollments.</p>
              ) : (
                <div className="space-y-3">
                  {enrollments.map((enrollment) => (
                    <div key={enrollment.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="font-bold text-gray-800">{enrollment.program}</h4>
                          <p className="text-sm text-gray-600">
                            Enrolled: {new Date(enrollment.date).toLocaleDateString()}
                          </p>
                          {enrollment.description && (
                            <p className="text-sm text-gray-700 mt-1">{enrollment.description}</p>
                          )}
                        </div>
                        <span className={`px-3 py-1 rounded text-sm ${
                          enrollment.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {enrollment.status || 'Active'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentProfile;





